import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRM, resolvePrivateImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Check, X } from "lucide-react";

export default function AdminBookings() {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [masters, setMasters] = useState([]);
  const [newSlot, setNewSlot] = useState({ dt: "", master_id: "" });
  const [loading, setLoading] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");
  const [viewProof, setViewProof] = useState(null);
  const [filterMaster, setFilterMaster] = useState("");

  const load = () => Promise.all([
    api.get("/booking-slots", { params: filterMaster ? { master_id: filterMaster } : {} }),
    api.get("/admin/bookings"),
    api.get("/masters", { params: { include_inactive: true } }),
  ]).then(([s, b, m]) => { setSlots(s.data); setBookings(b.data); setMasters(m.data); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterMaster]);

  const addSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.dt) return;
    if (!newSlot.master_id) {
      toast.error("请选择师傅 / Please pick a master");
      return;
    }
    setLoading(true);
    try {
      await api.post("/admin/booking-slots", {
        starts_at: new Date(newSlot.dt).toISOString(),
        duration_min: 60,
        active: true,
        master_id: newSlot.master_id,
      });
      toast.success(t("common.saved"));
      setNewSlot({ dt: "", master_id: newSlot.master_id });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const delSlot = async (id) => {
    try { await api.delete(`/admin/booking-slots/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const confirmBooking = async (id) => {
    await api.patch(`/admin/bookings/${id}/status`, { status: "confirmed" });
    toast.success(t("common.saved"));
    load();
  };
  const rejectBooking = async () => {
    await api.patch(`/admin/bookings/${rejectingId}/status`, { status: "rejected", reject_reason: reason });
    setRejectingId(null); setReason("");
    load();
  };

  const masterName = (id) => {
    const m = masters.find((x) => x.id === id);
    return m ? (zh ? m.name_zh : m.name_en) : "—";
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-bookings-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP3.bookings")}</h1>

      {/* Slots panel */}
      <div className="mt-8 rounded-lg border border-[#e5ebeb] bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold">{t("adminP3.slots")}</div>
          <select value={filterMaster} onChange={(e) => setFilterMaster(e.target.value)} className="text-xs px-3 py-1.5 rounded-md border border-[#e5ebeb]" data-testid="filter-master">
            <option value="">{t("masters.all")}</option>
            {masters.map((m) => <option key={m.id} value={m.id}>{zh ? m.name_zh : m.name_en}</option>)}
          </select>
        </div>
        <form onSubmit={addSlot} className="mt-3 flex flex-col sm:flex-row gap-2">
          <input type="datetime-local" value={newSlot.dt} onChange={(e) => setNewSlot({ ...newSlot, dt: e.target.value })} className="flex-1 px-4 py-2 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none" data-testid="new-slot-dt" />
          <select value={newSlot.master_id} onChange={(e) => setNewSlot({ ...newSlot, master_id: e.target.value })} className="sm:w-56 px-4 py-2 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none" data-testid="new-slot-master">
            <option value="">{t("masters.pickMaster")}</option>
            {masters.filter((m) => m.active).map((m) => <option key={m.id} value={m.id}>{zh ? m.name_zh : m.name_en}</option>)}
          </select>
          <button disabled={loading} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="add-slot-btn">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {t("adminP3.newSlot")}
          </button>
        </form>
        <div className="mt-4 flex flex-wrap gap-2 max-h-56 overflow-y-auto" data-testid="slots-list">
          {slots.map((s) => (
            <div key={s.id} className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5 ${s.is_booked ? "bg-amber-50 text-amber-800" : "bg-[#00868e]/10 text-[#00868e]"}`} data-testid={`slot-item-${s.id}`}>
              {new Date(s.starts_at).toLocaleString()}
              {s.master_id && <span className="text-[10px] opacity-70">· {masterName(s.master_id)}</span>}
              {!s.is_booked && <button onClick={() => delSlot(s.id)} className="ml-1"><X size={10} /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Bookings */}
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-bookings-table">
          <thead><tr className="text-left text-xs uppercase text-[#4a6e77]">
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Slot</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Master</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Customer</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Deposit</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]">Status</th>
            <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
          </tr></thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-${b.id}`}>
                <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{new Date(b.starts_at).toLocaleString()}</td>
                <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{zh ? (b.master_name_zh || "—") : (b.master_name_en || "—")}</td>
                <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">
                  <div>{b.user_name}</div>
                  <div className="text-xs">{b.user_email} · {b.phone}</div>
                  {b.contact_email && (
                    <div className="text-xs" data-testid={`admin-booking-contact-email-${b.id}`}>✉ {b.contact_email}</div>
                  )}
                  {b.birth_snapshot?.birth_year && (
                    <div className="text-[10px] mt-0.5 text-[#00868e]">
                      {t(`profile.${b.birth_snapshot.gender || "other"}`)} · {b.birth_snapshot.birth_year}-{String(b.birth_snapshot.birth_month).padStart(2,"0")}-{String(b.birth_snapshot.birth_day).padStart(2,"0")} {String(b.birth_snapshot.birth_hour).padStart(2,"0")}:00
                    </div>
                  )}
                </td>
                <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#005870] font-semibold">{formatRM(b.deposit || 0)}</td>
                <td className="py-3 px-4 border-b border-[#e5ebeb]">
                  <span className={`text-xs px-2 py-1 rounded-full ${b.status === "confirmed" ? "bg-[#00868e]/10 text-[#00868e]" : b.status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                    {t(`booking.statuses.${b.status}`)}
                  </span>
                </td>
                <td className="py-3 px-4 border-b border-[#e5ebeb]">
                  {b.payment_proof && <button onClick={() => setViewProof(b.payment_proof)} className="text-[#005870] hover:text-[#00868e] text-xs mr-2" data-testid={`booking-view-proof-${b.id}`}>Proof</button>}
                  {b.status !== "confirmed" && <button onClick={() => confirmBooking(b.id)} className="text-[#005870] text-xs mr-2" data-testid={`confirm-booking-${b.id}`}><Check size={12} className="inline"/> Confirm</button>}
                  {b.status !== "rejected" && <button onClick={() => { setRejectingId(b.id); setReason(""); }} className="text-red-700 text-xs" data-testid={`reject-booking-${b.id}`}><X size={12} className="inline"/> Reject</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rejectingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-sm text-[#4a6e77] mb-2">Reason</div>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 border border-[#e5ebeb] rounded-md" data-testid="booking-reject-reason" />
            <div className="mt-4 flex gap-2">
              <button onClick={rejectBooking} className="flex-1 px-4 py-2 rounded-full bg-red-600 text-white" data-testid="booking-reject-confirm">{t("common.confirm")}</button>
              <button onClick={() => setRejectingId(null)} className="flex-1 px-4 py-2 rounded-full border border-[#e5ebeb]">{t("common.cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {viewProof && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setViewProof(null)}>
          <img src={resolvePrivateImage(viewProof)} alt="proof" className="max-h-[80vh] max-w-full rounded" />
        </div>
      )}
    </div>
  );
}
